import { atom } from 'jotai';

import type {
  TYPE_DESIRED_COLOR_OPTIONS,
  TYPE_MATERIAL_OPTIONS,
} from '../constants/colors-and-materials';
import {
  type GoodWeatherDaylightValue,
  type GoodWeatherSeasonValue,
  type ROOM_MODE_OPTION_VALUE,
  ROOM_MODE_OPTIONS,
  ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES,
} from '../constants/room-mode';
import type { TYPE_DESIRED_STYLE_OPTIONS } from '../constants/room-style';
import type { TYPE_ROOM_TYPE_OPTIONS } from '../constants/room-type';
import { CHECKBOXES_OPTIONS, type TYPE_CHECKBOXES_OPTIONS } from '../constants/style-settings';
import {
  EDIT_IMAGE_METHOD,
  type TYPE_EDIT_IMAGE_METHOD,
  type TYPE_MAGIC_TOOL_OPTIONS,
} from '../constants/toggle-group-options';
import type { WatermarkPosition } from '@/utils/watermark';

export interface ImageRenderingSettings {
  editMethod: TYPE_EDIT_IMAGE_METHOD;
  magicToolOption: TYPE_MAGIC_TOOL_OPTIONS | null;
  roomMode: ROOM_MODE_OPTION_VALUE;
  roomType: TYPE_ROOM_TYPE_OPTIONS | undefined;
  desiredStyle: TYPE_DESIRED_STYLE_OPTIONS | undefined;
  desiredColors: TYPE_DESIRED_COLOR_OPTIONS[];
  materials: TYPE_MATERIAL_OPTIONS[];
  settingsChanged: boolean;
  checkboxes: TYPE_CHECKBOXES_OPTIONS[];
  additionalInfo: string;
  /** Only when roomMode === 'goodWeather' */
  goodWeatherDaylight?: GoodWeatherDaylightValue;
  goodWeatherSeason?: GoodWeatherSeasonValue;
}

export const defaultSettings: ImageRenderingSettings = {
  editMethod: EDIT_IMAGE_METHOD.STYLE,
  magicToolOption: null,
  roomMode: ROOM_MODE_OPTIONS.CHANGE_STYLE.value,
  roomType: undefined,
  desiredStyle: undefined,
  desiredColors: [],
  materials: [],
  settingsChanged: false,
  checkboxes: Object.values(CHECKBOXES_OPTIONS),
  additionalInfo: '',
  goodWeatherDaylight: undefined,
  goodWeatherSeason: undefined,
};

export const settingsAtom = atom<ImageRenderingSettings>(defaultSettings);

// Computed atom for checking if settings are filled
export const settingsFilledAtom = atom((get) => {
  const settings = get(settingsAtom);
  const { roomMode, roomType, desiredStyle } = settings;

  return (
    Boolean(roomMode) &&
    (!ROOM_MODE_OPTIONS_WITH_NO_CHECKBOXES.includes(roomMode)
      ? Boolean(desiredStyle) && Boolean(roomType)
      : true)
  );
});

// Helper atoms for backward compatibility (if needed)
export const editMethodAtom = atom(
  (get) => get(settingsAtom).editMethod,
  (get, set, value: TYPE_EDIT_IMAGE_METHOD) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, editMethod: value });
  }
);

export const magicToolOptionAtom = atom(
  (get) => get(settingsAtom).magicToolOption,
  (get, set, value: TYPE_MAGIC_TOOL_OPTIONS | null) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, magicToolOption: value });
  }
);

export const roomModeSettingsAtom = atom(
  (get) => get(settingsAtom).roomMode,
  (get, set, value: ROOM_MODE_OPTION_VALUE) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, roomMode: value });
  }
);

export const roomTypeSettingsAtom = atom(
  (get) => get(settingsAtom).roomType,
  (get, set, value: TYPE_ROOM_TYPE_OPTIONS | undefined) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, roomType: value, desiredColors: [], materials: [] });
  }
);

export const desiredStyleSettingsAtom = atom(
  (get) => get(settingsAtom).desiredStyle,
  (get, set, value: TYPE_DESIRED_STYLE_OPTIONS | undefined) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, desiredStyle: value });
  }
);

export const desiredColorSettingsAtom = atom(
  (get) => get(settingsAtom).desiredColors,
  (get, set, value: TYPE_DESIRED_COLOR_OPTIONS[]) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, desiredColors: value });
  }
);

export const materialSettingsAtom = atom(
  (get) => get(settingsAtom).materials,
  (get, set, value: TYPE_MATERIAL_OPTIONS[]) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, materials: value });
  }
);

export const settingsChangedAtom = atom(
  (get) => get(settingsAtom).settingsChanged,
  (get, set, value: boolean) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, settingsChanged: value });
  }
);

export const checkboxesSettingsAtom = atom(
  (get) => get(settingsAtom).checkboxes,
  (get, set, value: TYPE_CHECKBOXES_OPTIONS[]) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, checkboxes: value });
  }
);

export const additionalInfoAtom = atom(
  (get) => get(settingsAtom).additionalInfo,
  (get, set, value: string) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, additionalInfo: value });
  }
);

export const goodWeatherDaylightAtom = atom(
  (get) => get(settingsAtom).goodWeatherDaylight,
  (get, set, value: GoodWeatherDaylightValue | undefined) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, goodWeatherDaylight: value });
  }
);

export const goodWeatherSeasonAtom = atom(
  (get) => get(settingsAtom).goodWeatherSeason,
  (get, set, value: GoodWeatherSeasonValue | undefined) => {
    const settings = get(settingsAtom);
    set(settingsAtom, { ...settings, goodWeatherSeason: value });
  }
);

export const updateSingleCheckboxAtom = atom(
  null,
  (get, set, checkboxValue: string, isChecked: boolean, editable: boolean) => {
    const settings = get(settingsAtom);
    const currentCheckboxes = settings.checkboxes || [];

    const updatedCheckboxes = currentCheckboxes.map((checkbox) =>
      checkbox.value === checkboxValue
        ? { ...checkbox, checked: isChecked, editable: editable }
        : checkbox
    ) as TYPE_CHECKBOXES_OPTIONS[];

    set(settingsAtom, { ...settings, checkboxes: updatedCheckboxes });
  }
);

// Read-only selector that returns a function to retrieve a checkbox by its value
export const getCheckboxByValueAtom = atom((get) => {
  const checkboxes = get(settingsAtom).checkboxes || [];
  return (checkboxValue: string) =>
    checkboxes.find((checkbox) => checkbox.value === checkboxValue) as TYPE_CHECKBOXES_OPTIONS;
});

export const DEFAULT_WATERMARK_TEXT = 'Visualisierung durch KI\nAbweichung zur Realität möglich';

export const watermarkAtom = atom<{
  text: string;
  position: WatermarkPosition;
  applyWatermark: boolean;
}>({
  text: DEFAULT_WATERMARK_TEXT,
  position: 'top-right',
  applyWatermark: true,
});
